import { useState, useEffect, useCallback } from 'react';
import { getUserUsage } from '../utils/api';

/**
 * SINGLE SOURCE OF TRUTH for plan gating on the frontend.
 * 
 * All gating decisions must use this hook.
 * Do NOT use user.role from AuthContext for premium features.
 * 
 * Returns:
 * - usage: Raw usage object from API
 * - isFreeLimitReached: Boolean - show soft blur for blood/nutrition
 * - isSymptomLimitReached: Boolean - show soft blur for symptoms
 * - canAddEntry: Boolean - allow new blood/nutrition entry
 * - canAddSymptom: Boolean - allow new symptom entry
 * - canExport: Boolean - allow data export
 * - loading: Loading state
 * - error: Error state
 * - refetch: Function to manually refetch usage
 * 
 * Note: bypass_reason is returned by API for debugging but should NOT be shown in UI.
 */
const useUsageLimit = () => {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUsage = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getUserUsage();
      setUsage(data);
    } catch (err) {
      console.error('Failed to fetch usage:', err);
      setError(err);
      // Default to unrestricted on error (don't punish user for API issues)
      setUsage({
        total_entries: 0,
        symptom_entries: 0,
        free_limit: 3,
        free_symptom_limit: 6,
        free_limit_reached: false,
        symptom_limit_reached: false,
        can_add_entry: true,
        can_add_symptom: true,
        can_export: true,
        plan: 'free',
        bypass_reason: null
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  return {
    usage,
    // Primary gating flags - use these for all feature decisions
    isFreeLimitReached: usage?.free_limit_reached ?? false,
    isSymptomLimitReached: usage?.symptom_limit_reached ?? false,
    canAddEntry: usage?.can_add_entry ?? true,
    canAddSymptom: usage?.can_add_symptom ?? true,
    canExport: usage?.can_export ?? true,
    // Metadata
    totalEntries: usage?.total_entries ?? 0,
    symptomEntries: usage?.symptom_entries ?? 0,
    freeLimit: usage?.free_limit ?? 3,
    freeSymptomLimit: usage?.free_symptom_limit ?? 6,
    // State
    loading,
    error,
    refetch: fetchUsage
  };
};

export default useUsageLimit;
